import { Link } from 'react-router-dom'
import {Phone, Bath, Droplets, Flame, Plug, CheckCircle2, Calendar, Thermometer} from 'lucide-react'
import SectionHeader from '@/components/SectionHeader'

const installImg = 'https://static.lumi.new/27/27efd92bb3c7e2f2cedc3e487c17e15d.png'

const services = [
  {
    icon: Bath,
    title: "Pose WC & lavabo",
    desc: "Installation soignée de WC suspendus, classiques, lavabos, vasques et meubles de salle de bain.",
  },
  {
    icon: Droplets,
    title: "Rénovation salle de bain",
    desc: "Rénovation complète : douche, baignoire, plomberie, robinetterie, étanchéité.",
  },
  {
    icon: Flame,
    title: "Remplacement chauffe-eau",
    desc: "Conseil, fourniture et pose de chauffe-eau électriques, thermodynamiques ou à gaz.",
  },
  {
    icon: Thermometer,
    title: "Installation chauffage",
    desc: "Pose et remplacement de chaudières (gaz, mazout, électrique), radiateurs et plancher chauffant.",
  },
  {
    icon: Plug,
    title: "Raccordements",
    desc: "Raccordement machine à laver, lave-vaisselle, adoucisseur d'eau, équipements sanitaires.",
  },
]

export default function InstallationsPage() {
  return (
    <div>
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-urgent-50 border border-urgent-100 text-urgent-700 text-xs font-bold uppercase tracking-wider">
              Installations sanitaires
            </div>
            <h1 className="mt-5 text-4xl lg:text-5xl font-extrabold text-navy-700 tracking-tight leading-[1.05] text-balance">
              Installation sanitaire & chauffage — qualité suisse, finitions soignées.
            </h1>
            <p className="mt-5 text-lg text-navy-500 leading-relaxed">
              Du remplacement d'un WC à la rénovation complète de votre salle de bain ou la pose d'un nouveau système de chauffage : nos plombiers et chauffagistes réalisent vos installations avec rigueur, propreté et garantie.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a href="https://fixnow.ch" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow">
                <Calendar className="w-5 h-5" strokeWidth={2.5} />
                Voir notre disponibilité sur FixNow
              </a>
              <a href="tel:+41796430399" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-navy-700 text-white font-bold hover:bg-navy-600 transition-colors">
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                Nous appeler
              </a>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-elevated border border-navy-100">
            <img src={installImg} alt="Installation salle de bain moderne" className="w-full h-[400px] lg:h-[480px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-navy-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            eyebrow="Nos installations"
            title="Une expertise complète"
            subtitle="Que ce soit pour un remplacement ponctuel ou une rénovation totale, nous intervenons avec précision et garantie."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => (
              <div key={s.title} className="bg-white rounded-2xl p-6 border border-navy-100 hover:shadow-elevated transition-shadow">
                <div className="w-12 h-12 rounded-xl gradient-urgent flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-extrabold text-navy-700">{s.title}</h3>
                <p className="mt-1.5 text-sm text-navy-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <SectionHeader
            eyebrow="Notre processus"
            title="De la demande à la réalisation"
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: '01', t: 'Prise de contact', d: 'Vous nous décrivez votre projet par téléphone ou formulaire.' },
              { n: '02', t: 'Devis gratuit', d: 'Visite technique et devis détaillé sans engagement.' },
              { n: '03', t: 'Installation', d: 'Travaux réalisés dans les délais convenus, dans la propreté.' },
              { n: '04', t: 'Garantie', d: 'Réception de chantier et garantie sur les travaux et fournitures.' },
            ].map((step) => (
              <div key={step.n} className="bg-white rounded-2xl p-6 border border-navy-100 relative">
                <div className="text-4xl font-extrabold text-urgent-500/30">{step.n}</div>
                <div className="mt-2 font-extrabold text-navy-700">{step.t}</div>
                <div className="text-sm text-navy-500 mt-1.5">{step.d}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-navy-700 rounded-3xl p-8 lg:p-10 text-white">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
              <div>
                <h3 className="text-2xl lg:text-3xl font-extrabold">Un projet en tête ?</h3>
                <p className="mt-2 text-navy-100">Demandez votre devis gratuit et sans engagement.</p>
              </div>
              <a href="https://fixnow.ch" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow whitespace-nowrap">
                <Calendar className="w-5 h-5" strokeWidth={2.5} />
                Voir notre disponibilité sur FixNow
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
